"use client";

import { useState, useRef, useEffect, useId } from "react";
import { X, ImagePlus } from "lucide-react";
import { FieldError } from "@/components/ui";
import { useAuth } from "@/context/AuthContext/AuthContext";
import { useNewsStore } from "@/store/newsStore";
import { useGroupStore } from "@/store/groupStore";
import { useLanguage } from "@/context/LanguageContext";
import { ActivityLogger } from "@/lib/activityLog";
import { getStrapiImageUrl } from "@/lib/image-utils";
import {
  getImageUploadCopy,
  getImageUploadLimitMessage,
  MAX_MULTI_IMAGE_UPLOAD_COUNT,
  prepareImageUploads,
} from "@/lib/image-upload";
import { useContentModeration } from "@/hooks/useContentModeration";
import { CrisisModal } from "./CrisisModal";
import { InappropriateLanguageModal } from "./InappropriateLanguageModal";
import { InappropriateLanguageBanner } from "./InappropriateLanguageBanner";

interface CreateNewsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called with `true` after a successful create or update, so the parent can
   * confirm it. Never called with `false`: failures render inside the modal
   * (the submit banner and the photo field error), because a parent that
   * omits this prop -- NewsCard's edit flow -- would otherwise drop them.
   */
  onResult?: (success: boolean, message: string) => void;
  preselectedGroupId?: number;
  editMode?: boolean;
  newsDocumentId?: string;
  initialContent?: string;
  initialImages?: { id: number; url: string; alternativeText?: string }[];
  initialPreloadedFiles?: { file: File; preview: string }[];
}

const MAX_IMAGES = MAX_MULTI_IMAGE_UPLOAD_COUNT;
const MAX_CONTENT_LENGTH = 5000;

export default function CreateNewsModal({
  isOpen,
  onClose,
  onResult,
  preselectedGroupId,
  editMode = false,
  newsDocumentId,
  initialContent = '',
  initialImages = [],
  initialPreloadedFiles = [],
}: CreateNewsModalProps) {
  const { t, language } = useLanguage();
  const { currentUser } = useAuth();
  const { createNews, updateNews } = useNewsStore();
  const { getGroupsWhereUserCanPost, groups, myMemberships, fetchGroups, fetchMyMemberships } = useGroupStore();

  const [content, setContent] = useState(initialContent);
  const [images, setImages] = useState<File[]>(initialPreloadedFiles.map(p => p.file));
  const [imagePreviews, setImagePreviews] = useState<string[]>(initialPreloadedFiles.map(p => p.preview));
  const [imageNames, setImageNames] = useState<string[]>(initialPreloadedFiles.map(p => p.file.name));
  const [existingImages, setExistingImages] = useState(initialImages);
  const [dragActive, setDragActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(preselectedGroupId ?? null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Failures render here instead of going to the parent (see onResult).
  // submitError is the banner at the top of the form (React Standards 11.6: a
  // form-level error belongs above the form); photoError sits under the photo
  // picker, where the event edit form shows its own photo errors.
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const savingStatusId = useId();

  // Clear both errors when the modal deactivates. CreateButton renders this
  // component unconditionally as <CreateNewsModal isOpen={...} />, so it stays
  // MOUNTED while closed and its state survives; without this reset, an error
  // from one attempt would greet the next open. Adjusted during render, the
  // same "reset state when a prop changes" pattern CreateEventModal uses.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (wasOpen !== isOpen) {
    setWasOpen(isOpen);
    if (!isOpen) {
      setSubmitError(null);
      setPhotoError(null);
    }
  }
  const uploadCopy = getImageUploadCopy(language, 'multiple');
  const effectiveSelectedGroupId = preselectedGroupId ?? selectedGroupId ?? null;
  const selectedGroup = effectiveSelectedGroupId
    ? groups.find((group) => group.id === effectiveSelectedGroupId) ?? null
    : null;

  useEffect(() => {
    if (isOpen) {
      if (groups.length === 0) fetchGroups();
      if (currentUser && myMemberships.length === 0) fetchMyMemberships(currentUser.id);
    }
  }, [isOpen, groups.length, myMemberships.length, fetchGroups, fetchMyMemberships, currentUser]);

  const userGroups = currentUser ? getGroupsWhereUserCanPost(currentUser.id) : [];

  const {
    hasInappropriate,
    showCrisisModal,
    showInappropriateModal,
    dismissCrisisModal,
    dismissInappropriateModal,
  } = useContentModeration(content);

  if (!isOpen) return null;

  const totalImages = existingImages.length + images.length;

  const handleFiles = async (files: FileList | File[]) => {
    const allImageFiles = Array.from(files).filter((file) => file.type.startsWith('image/'));

    if (totalImages + allImageFiles.length > MAX_IMAGES) {
      setPhotoError(getImageUploadLimitMessage(MAX_IMAGES));
      return;
    }

    const { prepared, error } = await prepareImageUploads(allImageFiles, language);
    if (error) {
      setPhotoError(error);
      return;
    }

    setPhotoError(null);
    setImages(prev => [...prev, ...prepared]);
    setImageNames(prev => [...prev, ...prepared.map(f => f.name)]);

    prepared.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files) {
      void handleFiles(e.dataTransfer.files);
    }
  };

  const removeNewImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    setImageNames(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index: number) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!currentUser || !content.trim() || isSubmitting) return;

    if (content.length > MAX_CONTENT_LENGTH) {
      setSubmitError(t.modals.news.contentTooLong);
      return;
    }

    setSubmitError(null);
    setIsSubmitting(true);

    try {
      let result: { success: boolean; error?: string; documentId?: string };

      if (editMode && newsDocumentId) {
        result = await updateNews(newsDocumentId, {
          content: content.trim(),
          images: images.length > 0 ? images : undefined,
          imagesToKeep: existingImages.map(img => img.id),
        });
      } else {
        result = await createNews({
          content: content.trim(),
          images: images.length > 0 ? images : undefined,
          groupId: selectedGroup?.id,
          groupDocumentId: selectedGroup?.documentId,
        });
      }

      if (result.success) {
        onResult?.(true, editMode ? t.modals.news.postUpdated : t.modals.news.postCreated);
        const postTitle = content.trim().substring(0, 50) + (content.length > 50 ? '...' : '');
        if (editMode) {
          ActivityLogger.editPost(currentUser, postTitle, newsDocumentId);
        } else {
          // The group name makes a group's posts findable by name in the admin
          // activity log (#1403); the id links the entry back to the post.
          ActivityLogger.createPost(
            currentUser,
            postTitle,
            result.documentId,
            selectedGroup ? { name: selectedGroup.name, documentId: selectedGroup.documentId } : undefined,
          );
        }
        setContent('');
        setImages([]);
        setImagePreviews([]);
        setImageNames([]);
        setExistingImages([]);
        setSelectedGroupId(preselectedGroupId ?? null);
        onClose();
      } else {
        setSubmitError(result.error || (editMode ? t.modals.news.failedUpdate : t.modals.news.failedCreate));
      }
    } catch (error) {
      console.error('src/components/ui/CreateNewsModal.tsx', error);
      setSubmitError(t.errors.somethingWentWrong);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Every close path -- X, Cancel, backdrop -- goes through here. Closing
  // mid-save would unmount the modal (most parents render it conditionally)
  // while the uploads are still running, leaving the outcome nowhere to show.
  // X and Cancel are also disabled while saving; the backdrop is not a
  // control, so a click on it during a save is simply ignored.
  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const savingLabel = editMode ? t.modals.news.updating : t.modals.news.creating;

  const isValid = content.trim().length > 0
    && content.length <= MAX_CONTENT_LENGTH
    && !hasInappropriate
    && (editMode || selectedGroup !== null);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="create-news-root">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} data-testid="create-news-backdrop" />
      <div className="relative bg-background rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-background z-10">
          <div>
            <h2 className="text-lg font-semibold text-foreground" data-testid="create-news-heading">
              {editMode ? t.modals.news.editPost : t.modals.news.createPost}
            </h2>
            <p className="text-sm text-caption">
              {editMode ? t.modals.news.updateYourPost : t.modals.news.shareWithCommunity}
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label={t.common.close}
            aria-describedby={isSubmitting ? savingStatusId : undefined}
            className="p-1 hover:bg-sidebar-hover rounded transition disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="create-news-close-btn"
          >
            <X size={20} className="text-foreground-muted" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {submitError && (
            <div
              role="alert"
              className="px-3 py-2 bg-red-50 border border-red-300 rounded-lg text-sm text-red-700"
              data-testid="create-news-submit-error"
            >
              {submitError}
            </div>
          )}

          {!editMode && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                {t.modals.news.postingAsGroupRequired}
              </label>
              {preselectedGroupId ? (
                <div className="w-full px-3 py-2 border border-border rounded-md text-foreground bg-background-secondary">
                  {selectedGroup?.name || t.common.loading}
                </div>
              ) : (
                <select
                  value={selectedGroupId ?? ""}
                  onChange={(e) => setSelectedGroupId(Number(e.target.value) || null)}
                  className="w-full px-3 py-2 border border-border rounded-md text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="create-news-group-select"
                >
                  <option value="" disabled>{t.modals.news.selectGroup}</option>
                  {userGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              )}
              <p className="text-xs text-caption mt-1">
                {t.modals.news.selectGroupRequired}
              </p>
            </div>
          )}

          <CrisisModal
            open={showCrisisModal}
            onClose={dismissCrisisModal}
            testIdPrefix="create-news-crisis"
          />

          <InappropriateLanguageModal
            open={showInappropriateModal}
            onClose={dismissInappropriateModal}
            testIdPrefix="create-news-inappropriate"
          />

          {hasInappropriate && (
            <InappropriateLanguageBanner testIdPrefix="create-news-inappropriate" />
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t.modals.news.whatsOnYourMind} *
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t.modals.news.sharePlaceholder}
              rows={6}
              className="w-full px-3 py-2 border border-border rounded-md text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              data-testid="create-news-content-textarea"
            />
            <div className="flex justify-between items-center mt-1">
              <span className="text-xs text-caption" data-testid="create-news-character-counter">
                {content.length} / {MAX_CONTENT_LENGTH} {t.modals.news.characters}
              </span>
              {content.length > MAX_CONTENT_LENGTH && (
                <span className="text-xs text-red-500">{t.modals.news.contentTooLong}</span>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t.modals.news.imagesOptional} ({t.modals.news.max} {MAX_IMAGES})
            </label>

            {/* Existing Images */}
            {existingImages.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-2">
                {existingImages.map((img, index) => (
                  <div key={img.id} className="relative aspect-square rounded-md overflow-hidden border border-border">
                    <img
                      src={getStrapiImageUrl(img.url) || ''}
                      alt={img.alternativeText || `Image ${index + 1}`}
                      className="w-full h-full object-contain"
                    />
                    <button
                      onClick={() => removeExistingImage(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition"
                      aria-label="Remove image"
                      data-testid={`create-news-image-remove-btn-existing-${index}`}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* New Image Previews */}
            {imagePreviews.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-2">
                {imagePreviews.map((preview, index) => (
                  <div key={index} className="flex flex-col gap-1">
                    <div className="relative aspect-square rounded-md overflow-hidden border border-border">
                      <img src={preview} alt={`Preview ${index + 1}`} className="w-full h-full object-contain" />
                      <button
                        onClick={() => removeNewImage(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition"
                        aria-label="Remove image"
                        data-testid={`create-news-image-remove-btn-${index}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                    {imageNames[index] && <p className="text-xs text-caption truncate">{imageNames[index]}</p>}
                  </div>
                ))}
              </div>
            )}

            {/* Upload Area */}
            {totalImages < MAX_IMAGES && (
              <div
                onDrop={handleDrop}
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                className={`border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition ${
                  dragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                }`}
                onClick={() => fileInputRef.current?.click()}
                data-testid="create-news-drop-zone"
              >
                <ImagePlus className="mx-auto mb-2 text-foreground-muted" size={32} />
                <p className="text-sm text-foreground-muted mb-1">{uploadCopy.uploadPrompt}</p>
                <p className="text-xs text-caption">
                  {totalImages} / {MAX_IMAGES} {t.modals.news.imagesAdded}
                </p>
                <p className="text-xs text-caption mt-1">{uploadCopy.fileTypes}</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  multiple
                  onChange={(e) => {
                    const input = e.currentTarget;
                    if (input.files) {
                      // Copy the list, then clear the input, so choosing the
                      // same photo again (after removing it, or after an
                      // error) still fires a change event.
                      const files = Array.from(input.files);
                      input.value = '';
                      void handleFiles(files);
                    }
                  }}
                  className="hidden"
                  data-testid="create-news-file-input"
                />
              </div>
            )}
            {/* Outside the drop zone, which hides at MAX_IMAGES */}
            <FieldError data-testid="create-news-photo-error">{photoError}</FieldError>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-border sticky bottom-0 bg-background">
          {/* Why X and Cancel are disabled while saving, for screen readers */}
          <span id={savingStatusId} role="status" className="sr-only" data-testid="create-news-saving-status">
            {isSubmitting ? savingLabel : ''}
          </span>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            aria-describedby={isSubmitting ? savingStatusId : undefined}
            className="px-4 py-2 text-sm font-medium text-foreground hover:bg-sidebar-hover rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="create-news-cancel-btn"
          >
            {t.common.cancel}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="create-news-submit-btn"
          >
            {isSubmitting ? savingLabel : (editMode ? t.modals.news.updatePost : t.modals.news.createPostBtn)}
          </button>
        </div>
      </div>
    </div>
  );
}
